import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { 
  ArrowLeft, 
  Upload, 
  FileText, 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Briefcase, 
  GraduationCap,
  Send,
  CheckCircle,
  AlertCircle,
  Sparkles,
  Brain,
  Clock,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';

import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { ResponsiveContainer } from '../ui/ResponsiveContainer';
import { useDropzone } from 'react-dropzone';
import { apiClient } from '../../lib/api/client';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { useCVAnalysisStatus } from '../../hooks/useCVAnalysisStatus';
import { useEnhancedAutoFill } from '../../hooks/useEnhancedAutoFill';

// Enhanced candidate schema with all fields
const candidateSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255, 'Name cannot exceed 255 characters'),
  email: z.string().email('Please enter a valid email address'),
  phone: z.string().optional(),
  location: z.string().optional(),
  experience_years: z.number().min(0, 'Experience years cannot be negative').max(100, 'Experience years cannot exceed 100').optional(),
  current_position: z.string().optional(),
  current_company: z.string().optional(),
  education: z.string().optional(),
  skills: z.array(z.string()).optional(),
  certifications: z.array(z.string()).optional(),
  languages: z.array(z.string()).optional(),
  salary_expectation: z.number().min(0, 'Salary expectation cannot be negative').max(10000000, 'Salary expectation cannot exceed 10,000,000').optional(),
  availability: z.string().optional(),
  work_experience: z.array(z.object({
    company: z.string(),
    position: z.string(),
    duration: z.string(),
    description: z.string().optional()
  })).optional()
});

type CandidateFormData = z.infer<typeof candidateSchema>;

interface CandidateSubmissionPageProps {
  requirementId?: string;
}

interface UploadStatus {
  status: 'idle' | 'uploading' | 'processing' | 'completed' | 'error';
  message: string;
  progress?: number;
  candidateId?: string;
  startTime?: number;
  elapsedTime?: number;
}


export const CandidateSubmissionPage: React.FC<CandidateSubmissionPageProps> = ({ 
  requirementId: propRequirementId 
}) => {
  const FORM_ID = 'candidate-submission-form';
  const navigate = useNavigate();
  const { requirementId: paramRequirementId } = useParams();
  const requirementId = propRequirementId || paramRequirementId;

  // Form state
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    trigger,
    formState: { errors, isValid }
  } = useForm<CandidateFormData>({
    resolver: zodResolver(candidateSchema),
    defaultValues: {
      skills: [],
      certifications: [],
      languages: [],
      work_experience: []
    },
    mode: 'onChange'
  });

  // Debug form validation state
  React.useEffect(() => {
    console.log('🔍 Form validation state changed:', {
      isValid,
      errors,
      name: getValues('name'),
      email: getValues('email'),
      phone: getValues('phone')
    });
    
    // Debug specific validation errors
    if (Object.keys(errors).length > 0) {
      console.log('🔍 Validation errors detected:', {
        nameError: errors.name,
        emailError: errors.email,
        phoneError: errors.phone,
        allErrors: errors
      });
      
      // Debug: Show all error keys and values
      console.log('🔍 All validation error keys:', Object.keys(errors));
      console.log('🔍 All validation error values:', Object.values(errors));
      console.log('🔍 Full errors object:', errors);
    }
  }, [isValid, errors, getValues]);

  // Component state
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>({
    status: 'idle',
    message: 'Upload CV to auto-fill form'
  });
  
  // Timer ref for cleanup
  const timerRef = useRef<number | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [currentCandidateId, setCurrentCandidateId] = useState<string | null>(null);
  const [autoFilledFields, setAutoFilledFields] = useState<Set<string>>(new Set());
  const pollingStartedRef = useRef<boolean>(false);
  
  // Enhanced auto-fill hook
  const { autoFillFormFromAnalysis } = useEnhancedAutoFill(
    setValue,
    trigger,
    setAutoFilledFields
  );
  
  // CV analysis status hook
  const {
    isPolling: isAnalysisPolling,
    attempts: analysisAttempts,
    startTime: analysisStartTime,
  } = useCVAnalysisStatus(currentCandidateId, requirementId, {
    enabled: !!currentCandidateId && !pollingStartedRef.current, // Prevent multiple polling instances
    onComplete: async (status) => {
      console.log('🎉 CV analysis completed! Starting enhanced auto-fill...');
      
      // Mark polling as started to prevent multiple instances
      pollingStartedRef.current = true;
      
      // Update upload status
      const totalTime = analysisStartTime ? Math.floor((Date.now() - analysisStartTime) / 1000) : 0;
      const minutes = Math.floor(totalTime / 60);
      const seconds = totalTime % 60;
      const timeString = minutes > 0 ? `${minutes}:${seconds.toString().padStart(2, '0')}` : `${seconds}s`;
      
      setUploadStatus({
        status: 'completed',
        message: `CV analysis complete! (${timeString}) Auto-filling form with AI-enhanced data...`,
        progress: 100,
        candidateId: status.candidateId
      });
      
      // Auto-fill with AI analysis results
      await autoFillFormFromAnalysis(
        status.analysis_results || {},
        status.parsed_fields || {},
        {
          onComplete: () => {
            console.log('✅ Enhanced auto-fill completed');
          },
          onError: (error) => {
            console.error('❌ Enhanced auto-fill failed:', error);
          }
        }
      );
    },
    onError: (error) => {
      console.error('❌ CV analysis failed:', error);
      setUploadStatus({
        status: 'error',
        message: `Analysis failed: ${error.message}`
      });
    }
  });
  
  // Timer effect for processing status
  useEffect(() => {
    if (uploadStatus.status === 'processing' && uploadStatus.startTime) {
      timerRef.current = window.setInterval(() => {
        const elapsed = Math.floor((Date.now() - uploadStatus.startTime!) / 1000);
        const minutes = Math.floor(elapsed / 60);
        const seconds = elapsed % 60;
        const timeString = minutes > 0 ? `${minutes}:${seconds.toString().padStart(2, '0')}` : `${seconds}s`;
        
        setUploadStatus(prev => ({
          ...prev,
          elapsedTime: elapsed,
          message: `Analyzing the JD and CV (${timeString})`
        }));
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [uploadStatus.status, uploadStatus.startTime]);
  const uploadedFileRef = React.useRef<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [autoUploadCountdown, setAutoUploadCountdown] = useState<number | null>(null);
  const [autoUploadTimer, setAutoUploadTimer] = useState<number | null>(null);

  // API hooks - requirements data removed as it's not used

  // CV Upload handling
  const onDrop = (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    console.log('📁 File dropped:', file);
    if (file) {
      // Additional client-side validation for file extension
      const fileName = file.name.toLowerCase();
      const allowedExtensions = ['.pdf', '.docx'];
      const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
      
      if (!allowedExtensions.includes(fileExtension)) {
        toast.error('Only PDF and DOCX files are allowed');
        return;
      }
      
      console.log('📁 Setting uploaded file:', file.name);
      setUploadedFile(file);
      uploadedFileRef.current = file; // Store in ref as backup
      setUploadStatus({
        status: 'idle',
        message: `Selected: ${file.name}`
      });
      
      // Start auto-upload countdown
      startAutoUploadCountdown();
    }
  };

  // Auto-upload countdown logic
  const startAutoUploadCountdown = () => {
    console.log('🚀 Starting auto-upload countdown');
    setAutoUploadCountdown(5);
    
    const timer = setInterval(() => {
      setAutoUploadCountdown(prev => {
        console.log(`⏰ Countdown: ${prev}`);
        if (prev === null || prev <= 1) {
          console.log('🔥 Auto-upload triggered!');
          clearInterval(timer);
          setAutoUploadTimer(null);
          setAutoUploadCountdown(null);
          // Auto-upload the file
          handleCVUpload();
          return null;
        }
        return prev - 1;
      });
    }, 1000);
    
    setAutoUploadTimer(timer as any);
  };

  const cancelAutoUpload = () => {
    if (autoUploadTimer) {
      clearInterval(autoUploadTimer);
      setAutoUploadTimer(null);
    }
    setAutoUploadCountdown(null);
  };

  // Cleanup timer on unmount
  React.useEffect(() => {
    return () => {
      if (autoUploadTimer) {
        clearInterval(autoUploadTimer);
      }
    };
  }, [autoUploadTimer]);

  // Debug uploadedFile state changes
  React.useEffect(() => {
    console.log('📁 uploadedFile state changed:', uploadedFile);
  }, [uploadedFile]);

  // Debug countdown state changes
  React.useEffect(() => {
    console.log('🔍 Auto-upload countdown state changed:', autoUploadCountdown);
  }, [autoUploadCountdown]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024, // 10MB
  });

  const handleCVUpload = async () => {
    console.log('📤 handleCVUpload called');
    console.log('📤 Current uploadedFile state:', uploadedFile);
    console.log('📤 Current uploadedFileRef:', uploadedFileRef.current);
    
    // Use ref as fallback if state is null
    const fileToUpload = uploadedFile || uploadedFileRef.current;
    if (!fileToUpload) {
      console.log('❌ No uploaded file found in state or ref');
      return;
    }

    console.log('📤 Starting CV upload for file:', fileToUpload.name);
    setUploadStatus({
      status: 'uploading',
      message: 'Uploading CV and starting AI analysis...',
      progress: 0
    });
    console.log('📤 Upload status set to uploading');

    try {
      // Upload CV
      console.log('📤 Calling apiClient.uploadCV...');
      const response = await apiClient.uploadCV(fileToUpload, undefined, requirementId);
      console.log('📤 Upload response:', response);
      
      setUploadStatus({
        status: 'processing',
        message: 'AI analysis in progress... This may take 1-2 minutes.',
        progress: 50,
        candidateId: response.candidate_id,
        startTime: Date.now(),
        elapsedTime: 0
      });
      console.log('📤 Upload status set to processing');

      // Set candidate ID to trigger analysis status polling
      setCurrentCandidateId(response.candidate_id);
      console.log('🔄 Started analysis status polling for candidate:', response.candidate_id);

    } catch (error: any) {
      console.error('❌ CV upload failed:', error);
      setUploadStatus({
        status: 'error',
        message: error.response?.data?.detail || 'Upload failed'
      });
      toast.error('Failed to upload CV');
    }
  };

  // Note: Old polling and auto-fill functions removed - now using enhanced hooks

  // Form submission
  const onSubmit = async (data: CandidateFormData) => {
    console.log('🚀 Form submission started:', { data, requirementId, isValid });
    console.log('🔍 Form validation state:', { isValid, errors });
    console.log('🔍 Required fields check:', { 
      name: data.name, 
      email: data.email, 
      hasName: !!data.name, 
      hasEmail: !!data.email 
    });
    // Note: If requirementId is not provided, we'll try to infer a route after save

    // Validate only mandatory fields (name and email)
    if (!data.name || !data.email) {
      toast.error('Name and email are required');
      return;
    }

    // Helper function to convert string to array
    const stringToArray = (value: any): string[] => {
      if (Array.isArray(value)) return value;
      if (typeof value === 'string') return value.split(',').map(s => s.trim()).filter(s => s);
      return [];
    };

    // Convert and validate data types before sending to API
    const roundTo2 = (v: number) => Math.round(v * 100) / 100;
    const validatedData = {
      ...data,
      experience_years: (data.experience_years !== undefined && data.experience_years !== null && !Number.isNaN(data.experience_years))
        ? roundTo2(Number(data.experience_years))
        : undefined,
      salary_expectation: data.salary_expectation ? Number(data.salary_expectation) : undefined,
      skills: stringToArray(data.skills),
      certifications: stringToArray(data.certifications),
      languages: stringToArray(data.languages),
      work_experience: Array.isArray(data.work_experience) ? data.work_experience : []
    };

    // Debug: Log validated data types
    console.log('🔍 Validated data types:', {
      experience_years: validatedData.experience_years,
      experience_years_type: typeof validatedData.experience_years,
      salary_expectation: validatedData.salary_expectation,
      salary_expectation_type: typeof validatedData.salary_expectation,
      skills: validatedData.skills,
      skills_type: Array.isArray(validatedData.skills),
      work_experience: validatedData.work_experience,
      work_experience_type: Array.isArray(validatedData.work_experience)
    });

    setIsSubmitting(true);

    try {
      let candidateId = uploadStatus.candidateId;
      console.log('🔍 Candidate ID from upload status:', candidateId);

      // If no candidate exists (no CV uploaded), create one
      if (!candidateId) {
        console.log('📝 Creating new candidate...');
        const candidate = await apiClient.createCandidate(validatedData, requirementId);
        candidateId = candidate.id;
        console.log('✅ New candidate created:', candidateId);
      } else {
        console.log('🔄 Updating existing candidate:', candidateId);
        await apiClient.updateCandidate(candidateId, validatedData);
        console.log('✅ Candidate updated successfully');
      }

      // Note: Stage 2 processing is no longer needed with the new single-phase JD vs CV analysis workflow
      // The analysis is already completed during CV upload and auto-fill

      if (requirementId) {
        // Submit candidate to requirement
        await apiClient.submitCandidate({
          candidate_id: candidateId,
          requirement_id: requirementId
        });
        toast.success('Candidate submitted successfully!');
        // Redirect to analysis page for the provided requirement
        console.log('🧭 Navigating to analysis page:', `/candidates/${candidateId}/analysis/${requirementId}`);
        try {
          navigate(`/candidates/${candidateId}/analysis/${requirementId}`);
          console.log('✅ Navigation successful');
        } catch (error) {
          console.error('❌ Navigation failed:', error);
          toast.error('Failed to navigate to analysis page');
        }
      } else {
        // No requirement provided: try to find an analysis/submission to route to
        try {
          const analyses = await apiClient.getCandidateAnalyses(candidateId);
          const target = analyses?.analyses?.find((a: any) => a.status === 'completed') || analyses?.analyses?.[0];
          if (target?.requirement_id) {
            navigate(`/candidates/${candidateId}/analysis/${target.requirement_id}`);
            return;
          }
        } catch (e) {
          console.warn('No analyses found for candidate, checking submissions');
        }
        try {
          const candidate = await apiClient.getCandidate(candidateId);
          const sub = candidate?.submissions?.[0];
          if (sub?.requirement_id) {
            navigate(`/candidates/${candidateId}/analysis/${sub.requirement_id}`);
            return;
          }
        } catch (e) {
          console.warn('No submissions found for candidate');
        }
        // Fallback: candidate details page
        toast.success('Candidate updated successfully');
        navigate(`/candidates/${candidateId}`);
      }

    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to submit candidate');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Removed pollAnalysisStatus function - no longer needed since manual analysis button was removed

  const getUploadStatusIcon = () => {
    switch (uploadStatus.status) {
      case 'uploading':
      case 'processing':
        return <LoadingSpinner size="sm" variant="ai" className="text-blue-600" />;
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Upload className="w-5 h-5 text-gray-400 dark:text-gray-500" />;
    }
  };

  // Removed getAnalysisStatusIcon function - no longer needed since analysis sections were removed

  // Component to show auto-fill indicator
  const AutoFillIndicator: React.FC<{ fieldName: string }> = ({ fieldName }) => {
    if (!autoFilledFields.has(fieldName)) return null;
    
    return (
      <div className="flex items-center gap-1 text-green-600 text-xs mt-1">
        <CheckCircle className="w-3 h-3" />
        <span>Auto-filled from CV</span>
      </div>
    );
  };

  // Component to wrap input with auto-fill highlighting
  const AutoFillWrapper: React.FC<{ fieldName: string; children: React.ReactNode }> = ({ fieldName, children }) => {
    const isAutoFilled = autoFilledFields.has(fieldName);
    
    return (
      <div className={`relative ${isAutoFilled ? 'ring-2 ring-green-400/50 bg-green-50/20 dark:bg-green-900/10 rounded-lg p-1' : ''}`}>
        {children}
      </div>
    );
  };

  // Function to handle manual field edits (remove auto-fill indicator)
  const handleFieldEdit = (fieldName: string) => {
    setAutoFilledFields(prev => {
      const newSet = new Set(prev);
      newSet.delete(fieldName);
      return newSet;
    });
  };


  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Liquid Flow Background */}
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-primary-500/20 via-secondary-500/15 to-primary-600/10 rounded-full blur-3xl animate-liquid-flow opacity-60"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-gradient-to-tr from-secondary-500/20 via-primary-500/15 to-secondary-600/10 rounded-full blur-3xl animate-liquid-flow opacity-50" style={{ animationDelay: '2s' }}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-gradient-to-r from-primary-400/10 via-secondary-400/10 to-primary-500/10 rounded-full blur-2xl animate-floating opacity-40" style={{ animationDelay: '4s' }}></div>
        
        {/* Floating Particles */}
        <div className="absolute top-20 left-1/4 w-2 h-2 bg-primary-400/60 rounded-full animate-floating" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-40 right-1/3 w-1 h-1 bg-secondary-400/80 rounded-full animate-floating" style={{ animationDelay: '3s' }}></div>
        <div className="absolute bottom-32 left-1/3 w-1.5 h-1.5 bg-primary-300/70 rounded-full animate-floating" style={{ animationDelay: '5s' }}></div>
        <div className="absolute top-1/3 right-1/4 w-1 h-1 bg-secondary-300/90 rounded-full animate-floating" style={{ animationDelay: '2.5s' }}></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10">
        <ResponsiveContainer>
          <div className="space-y-6">

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Main Form */}
              <div className="lg:col-span-2">
                <div className="glass-card p-8 animate-fade-in-up">

                  {/* Action Buttons at Top */}
                  <div className="flex items-center justify-between gap-4 mb-8 pb-6 border-b border-glass-border">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate(-1)}
                      disabled={isSubmitting}
                      className="flex items-center gap-2 glass-button hover:glass-button-hover transition-all duration-300"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Back
                    </Button>
                    <div className="flex items-center gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => navigate(-1)}
                        disabled={isSubmitting}
                        className="glass-button hover:glass-button-hover transition-all duration-300"
                      >
                        Cancel
                      </Button>
                      {/* Debug info */}
                      <div className="text-xs text-text-enterprise-tertiary">
                        Form valid: {isValid ? '✅' : '❌'} | Submitting: {isSubmitting ? '⏳' : '⏸️'}
                      </div>
                      <Button
                        type="submit"
                        form={FORM_ID}
                        disabled={!isValid || isSubmitting}
                        className="flex items-center gap-2 glass-button-primary hover:glass-button-primary-hover transition-all duration-300"
                        onClick={() => {
                          console.log('🔘 Update Candidate button clicked');
                          console.log('🔍 Button state:', { isValid, isSubmitting, disabled: !isValid || isSubmitting });
                          console.log('🔍 Form values:', getValues());
                        }}
                      >
                        {isSubmitting ? (
                          <>
                            <LoadingSpinner size="sm" variant="ai" />
                            Updating...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4" />
                            Update Candidate
                          </>
                        )}
                      </Button>
                    </div>
                  </div>

                  <form id={FORM_ID} onSubmit={handleSubmit(onSubmit)} className="space-y-8">
                    {/* Basic Information */}
                    <div className="space-y-6">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-gradient-to-br from-primary-500/20 to-secondary-500/20 rounded-xl">
                          <User className="w-6 h-6 text-primary-400" />
                        </div>
                        <h3 className="text-xl font-bold text-text-enterprise-primary">Basic Information</h3>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="group">
                          <label className="block text-sm font-semibold text-text-enterprise-primary mb-3">
                            Full Name *
                          </label>
                          <AutoFillWrapper fieldName="name">
                            <div className="relative">
                              <User className="absolute left-4 top-1/2 transform -translate-y-1/2 text-text-enterprise-tertiary group-focus-within:text-primary-400 transition-colors duration-300 w-5 h-5" />
                              <Input
                                type="text"
                                placeholder="Enter full name"
                                className="pl-12 glass-input focus:glass-input-focus transition-all duration-300"
                                {...register('name')}
                                onChange={(e) => {
                                  register('name').onChange(e);
                                  handleFieldEdit('name');
                                }}
                              />
                            </div>
                          </AutoFillWrapper>
                          {errors.name && (
                            <p className="text-red-400 text-sm mt-2 flex items-center gap-1">
                              <AlertCircle className="w-4 h-4" />
                              {errors.name.message}
                            </p>
                          )}
                          <AutoFillIndicator fieldName="name" />
                        </div>

                        <div className="group">
                          <label className="block text-sm font-semibold text-text-enterprise-primary mb-3">
                            Email Address *
                          </label>
                          <AutoFillWrapper fieldName="email">
                            <div className="relative">
                              <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-text-enterprise-tertiary group-focus-within:text-primary-400 transition-colors duration-300 w-5 h-5" />
                              <Input
                                type="email"
                                placeholder="Enter email address"
                                className="pl-12 glass-input focus:glass-input-focus transition-all duration-300"
                                {...register('email')}
                              />
                            </div>
                          </AutoFillWrapper>
                          {errors.email && (
                            <p className="text-red-400 text-sm mt-2 flex items-center gap-1">
                              <AlertCircle className="w-4 h-4" />
                              {errors.email.message}
                            </p>
                          )}
                          <AutoFillIndicator fieldName="email" />
                        </div>

                        <div className="group">
                          <label className="block text-sm font-semibold text-text-enterprise-primary mb-3">
                            Phone Number
                          </label>
                          <AutoFillWrapper fieldName="phone">
                            <div className="relative">
                              <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 text-text-enterprise-tertiary group-focus-within:text-primary-400 transition-colors duration-300 w-5 h-5" />
                              <Input
                                type="tel"
                                placeholder="Enter phone number"
                                className="pl-12 glass-input focus:glass-input-focus transition-all duration-300"
                                {...register('phone')}
                              />
                            </div>
                          </AutoFillWrapper>
                          <AutoFillIndicator fieldName="phone" />
                        </div>

                        <div className="group">
                          <label className="block text-sm font-semibold text-text-enterprise-primary mb-3">
                            Location
                          </label>
                          <AutoFillWrapper fieldName="location">
                            <div className="relative">
                              <MapPin className="absolute left-4 top-1/2 transform -translate-y-1/2 text-text-enterprise-tertiary group-focus-within:text-primary-400 transition-colors duration-300 w-5 h-5" />
                              <Input
                                type="text"
                                placeholder="Enter location"
                                className="pl-12 glass-input focus:glass-input-focus transition-all duration-300"
                                {...register('location')}
                              />
                            </div>
                          </AutoFillWrapper>
                          <AutoFillIndicator fieldName="location" />
                        </div>
                  </div>
                </div>

                    {/* Professional Information */}
                    <div className="space-y-6">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-gradient-to-br from-secondary-500/20 to-primary-500/20 rounded-xl">
                          <Briefcase className="w-6 h-6 text-secondary-400" />
                        </div>
                        <h3 className="text-xl font-bold text-text-enterprise-primary">Professional Information</h3>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="group">
                          <label className="block text-sm font-semibold text-text-enterprise-primary mb-3">
                            Years of Experience
                          </label>
                          <AutoFillWrapper fieldName="experience_years">
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              placeholder="Enter years of experience"
                              className="glass-input focus:glass-input-focus transition-all duration-300"
                              {...register('experience_years', { valueAsNumber: true })}
                            />
                          </AutoFillWrapper>
                          <AutoFillIndicator fieldName="experience_years" />
                        </div>

                        <div className="group">
                          <label className="block text-sm font-semibold text-text-enterprise-primary mb-3">
                            Current Position
                          </label>
                          <AutoFillWrapper fieldName="current_position">
                            <div className="relative">
                              <Briefcase className="absolute left-4 top-1/2 transform -translate-y-1/2 text-text-enterprise-tertiary group-focus-within:text-primary-400 transition-colors duration-300 w-5 h-5" />
                              <Input
                                type="text"
                                placeholder="Enter current position"
                                className="pl-12 glass-input focus:glass-input-focus transition-all duration-300"
                                {...register('current_position')}
                              />
                            </div>
                          </AutoFillWrapper>
                          <AutoFillIndicator fieldName="current_position" />
                        </div>

                        <div className="group">
                          <label className="block text-sm font-semibold text-text-enterprise-primary mb-3">
                            Current Company
                          </label>
                          <AutoFillWrapper fieldName="current_company">
                            <Input
                              type="text"
                              placeholder="Enter current company"
                              className="glass-input focus:glass-input-focus transition-all duration-300"
                              {...register('current_company')}
                            />
                          </AutoFillWrapper>
                          <AutoFillIndicator fieldName="current_company" />
                        </div>

                        <div className="group">
                          <label className="block text-sm font-semibold text-text-enterprise-primary mb-3">
                            Education
                          </label>
                          <AutoFillWrapper fieldName="education">
                            <div className="relative">
                              <GraduationCap className="absolute left-4 top-1/2 transform -translate-y-1/2 text-text-enterprise-tertiary group-focus-within:text-primary-400 transition-colors duration-300 w-5 h-5" />
                              <Input
                                type="text"
                                placeholder="Enter education"
                                className="pl-12 glass-input focus:glass-input-focus transition-all duration-300"
                                {...register('education')}
                              />
                            </div>
                          </AutoFillWrapper>
                          <AutoFillIndicator fieldName="education" />
                        </div>
                  </div>
                </div>

                    {/* Skills */}
                    <div className="space-y-6">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-gradient-to-br from-primary-500/20 to-secondary-500/20 rounded-xl">
                          <Sparkles className="w-6 h-6 text-primary-400" />
                        </div>
                        <h3 className="text-xl font-bold text-text-enterprise-primary">Skills</h3>
                      </div>
                      <div className="group">
                        <label className="block text-sm font-semibold text-text-enterprise-primary mb-3">
                          Technical Skills
                        </label>
                        <AutoFillWrapper fieldName="skills">
                          <Input
                            type="text"
                            placeholder="Enter skills separated by commas"
                            className="glass-input focus:glass-input-focus transition-all duration-300"
                            value={Array.isArray(getValues('skills')) ? getValues('skills')?.join(', ') || '' : (getValues('skills') || '')}
                            onChange={(e) => {
                              const value = e.target.value;
                              const skillsArray = value ? value.split(',').map(s => s.trim()).filter(s => s) : [];
                              setValue('skills', skillsArray);
                              trigger('skills');
                            }}
                          />
                        </AutoFillWrapper>
                        <AutoFillIndicator fieldName="skills" />
                        <p className="text-sm text-text-enterprise-tertiary mt-2 flex items-center gap-1">
                          <Sparkles className="w-4 h-4" />
                          Separate multiple skills with commas
                        </p>
                      </div>
                    </div>



                  </form>
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* CV Upload */}
                <div className="glass-card p-6 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-gradient-to-br from-primary-500/20 to-secondary-500/20 rounded-xl">
                      <Upload className="w-6 h-6 text-primary-400" />
                    </div>
                    <h3 className="text-xl font-bold text-text-enterprise-primary">CV Upload</h3>
                  </div>
                  
                  {!uploadedFile ? (
                    <div
                      {...getRootProps()}
                      className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-300 glass-surface/30 hover:glass-surface/50 ${
                        isDragActive
                          ? 'border-primary-400 bg-primary-500/10 scale-105'
                          : 'border-glass-border hover:border-primary-400 hover:scale-102'
                      }`}
                    >
                      <input {...getInputProps()} />
                      <Upload className="w-12 h-12 text-text-enterprise-tertiary mx-auto mb-4 animate-bounce" />
                      <p className="text-text-enterprise-primary font-medium mb-2">
                        {isDragActive ? 'Drop the CV file here' : 'Drag & drop a CV file here'}
                      </p>
                      <p className="text-sm text-text-enterprise-tertiary">
                        or click to select a file
                      </p>
                      <p className="text-xs text-text-enterprise-tertiary mt-3 px-3 py-1 bg-glass-surface/50 rounded-full inline-block">
                        Supports PDF, DOCX, DOC (max 10MB)
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      <div className="flex items-center gap-3 p-4 glass-surface/30 rounded-xl">
                        <FileText className="w-6 h-6 text-primary-400" />
                        <div className="flex-1">
                          <p className="font-semibold text-text-enterprise-primary">{uploadedFile.name}</p>
                          <p className="text-sm text-text-enterprise-tertiary">
                            {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 p-4 glass-surface/30 rounded-xl">
                        {getUploadStatusIcon()}
                        <div className="flex-1">
                          <p className="text-sm font-medium text-text-enterprise-primary">
                            {uploadStatus.message}
                          </p>
                          {uploadStatus.progress !== undefined && (
                            <div className="w-full bg-glass-surface/50 rounded-full h-2 mt-3">
                              <div
                                className="bg-gradient-to-r from-primary-500 to-secondary-500 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${uploadStatus.progress}%` }}
                              />
                            </div>
                          )}
                          
                          {/* Enhanced progress indicators for AI analysis */}
                          {uploadStatus.status === 'processing' && isAnalysisPolling && (
                            <div className="mt-4 space-y-3">
                              <div className="flex items-center gap-2 text-xs text-text-enterprise-secondary">
                                <Brain className="w-4 h-4 text-primary-400" />
                                <span>AI analysis in progress...</span>
                              </div>
                              <div className="flex items-center gap-2 text-xs text-text-enterprise-secondary">
                                <Clock className="w-4 h-4 text-secondary-400" />
                                <span>Attempt {analysisAttempts}/60 • Auto-fill will start when complete</span>
                              </div>
                              <div className="text-xs text-text-enterprise-tertiary">
                                This typically takes 1-2 minutes. Form will auto-fill with AI-enhanced data.
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {(uploadStatus.status === 'idle' || uploadStatus.status === 'uploading' || uploadStatus.status === 'processing') && (
                        <div className="space-y-4">
                          {uploadStatus.status === 'idle' && autoUploadCountdown !== null ? (
                            <div className="space-y-4">
                              <div className="text-center p-4 glass-surface/30 rounded-xl">
                                <div className="text-sm text-text-enterprise-primary mb-3 font-medium">
                                  Auto-uploading in {autoUploadCountdown} seconds...
                                </div>
                                <div className="w-full bg-glass-surface/50 rounded-full h-2">
                                  <div
                                    className="bg-gradient-to-r from-primary-500 to-secondary-500 h-2 rounded-full transition-all duration-1000"
                                    style={{ width: `${((5 - autoUploadCountdown) / 5) * 100}%` }}
                                  />
                                </div>
                              </div>
                              <Button 
                                onClick={cancelAutoUpload} 
                                variant="outline" 
                                className="w-full glass-button hover:glass-button-hover transition-all duration-300"
                              >
                                Cancel Auto-Upload
                              </Button>
                            </div>
                          ) : uploadStatus.status === 'idle' ? (
                            <div className="text-center p-4 glass-surface/30 rounded-xl">
                              <div className="text-sm text-text-enterprise-primary font-medium">
                                File selected. Upload will start automatically...
                              </div>
                            </div>
                          ) : null}
                        </div>
                      )}
                    </div>
                  )}
                </div>

              </div>
            </div>
          </div>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
